<script setup lang="ts">
/**
 * La rareté d'un objet de la boutique d'avatars (HARMONIA-48).
 *
 * Elle vit en base depuis janvier (`shop_items.rarity`, 19 basiques, 19
 * standards, 6 rares), elle voyage jusqu'aux stores et jusqu'à la mesure, et
 * elle n'était affichée nulle part. Seul le niveau le plus rare se voyait, sous
 * la forme d'un « + » qui ouvrait le paywall : un marqueur d'ABONNEMENT déguisé
 * en rareté. Ici on sépare les deux. Cette pastille dit ce qu'un objet vaut dans
 * la collection ; le badge H+ dit ce qui se paie, et il reste où il est.
 *
 * D'où l'interdiction de la couleur `premium` (le violet d'Harmonia+) : un objet
 * rare est doré, pas abonné.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = withDefaults(defineProps<{
  /** `basic` | `standard` | `premium` ; toute autre valeur ne rend rien. */
  rarity?: string | null
  /** `pill` nomme la rareté, `dot` la réduit à un point (vignettes serrées). */
  variant?: 'pill' | 'dot'
}>(), {
  rarity: null,
  variant: 'pill',
})

const { t } = useI18n()

const TONES: Record<string, string> = {
  // Neutre : la rareté la plus commune ne doit pas attirer l'œil.
  basic: 'bg-warm-2 text-ink-3 border-line',
  standard: 'bg-brand-soft text-brand-deep border-[#CFE3D6]',
  // Doré, jamais violet : le violet appartient à Harmonia+.
  premium: 'bg-joy-soft text-[#8B5A0F] border-[#EBD9A8]',
}

const known = computed(() => !!props.rarity && props.rarity in TONES)
const label = computed(() => (known.value ? t(`shop.rarity.${props.rarity}`) : ''))
const tone = computed(() => (known.value ? TONES[props.rarity as string] : ''))
</script>

<template>
  <span v-if="known" data-testid="rarity-badge" :data-rarity="rarity" :aria-label="label" :title="label"
    class="inline-flex shrink-0 items-center justify-center border font-semibold" :class="[
      tone,
      variant === 'dot'
        ? 'size-3 rounded-full'
        : 'gap-1 rounded-full px-2 py-0.5 text-[11px] uppercase tracking-[0.06em]',
    ]">
    <template v-if="variant === 'pill'">{{ label }}</template>
  </span>
</template>
